import { BooleanInput, Button, ColorPicker, Container, Label, SelectInput, SliderInput } from '@playcanvas/pcui';
import { Color } from 'playcanvas';

import { Events } from '../events';
import { ShortcutManager } from '../shortcut-manager';
import { DEFAULT_PRESETS, type WalkPreset } from '../walk-presets';
import { localize } from './localization';
import { Tooltips } from './tooltips';

class ViewPanel extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'view-panel',
            class: 'panel',
            hidden: true
        };

        super(args);

        // stop pointer events bubbling
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        // header

        const header = new Container({
            class: 'panel-header'
        });

        const icon = new Label({
            text: '\uE403',
            class: 'panel-header-icon'
        });

        const label = new Label({
            text: localize('panel.view-options'),
            class: 'panel-header-label'
        });

        header.append(icon);
        header.append(label);

        // colors

        const clrRow = new Container({
            class: 'view-panel-row'
        });

        const clrLabel = new Label({
            text: localize('panel.view-options.colors'),
            class: 'view-panel-row-label'
        });

        const clrPickers = new Container({
            class: 'view-panel-row-pickers'
        });

        const bgClrPicker = new ColorPicker({
            class: 'view-panel-row-picker',
            channels: 3,
            value: [0, 0, 0]
        });

        const selectedClrPicker = new ColorPicker({
            class: 'view-panel-row-picker',
            channels: 4,
            value: [0, 0, 0, 1]
        });

        const unselectedClrPicker = new ColorPicker({
            class: 'view-panel-row-picker',
            channels: 4,
            value: [0, 0, 0, 1]
        });

        const lockedClrPicker = new ColorPicker({
            class: 'view-panel-row-picker',
            channels: 4,
            value: [0, 0, 0, 1]
        });

        const toArray = (clr: Color) => {
            return [clr.r, clr.g, clr.b, clr.a];
        };

        events.on('bgClr', (clr: Color) => {
            bgClrPicker.value = toArray(clr);
        });

        events.on('selectedClr', (clr: Color) => {
            selectedClrPicker.value = toArray(clr);
        });

        events.on('unselectedClr', (clr: Color) => {
            unselectedClrPicker.value = toArray(clr);
        });

        events.on('lockedClr', (clr: Color) => {
            lockedClrPicker.value = toArray(clr);
        });

        clrPickers.append(bgClrPicker);
        clrPickers.append(selectedClrPicker);
        clrPickers.append(unselectedClrPicker);
        clrPickers.append(lockedClrPicker);

        clrRow.append(clrLabel);
        clrRow.append(clrPickers);

        // picture mode and controls

        const pictureModeRow = new Container({
            class: 'view-panel-row'
        });

        const pictureModeLabel = new Label({
            text: 'Picture Mode',
            class: 'view-panel-row-label'
        });

        const pictureModeToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        pictureModeRow.append(pictureModeLabel);
        pictureModeRow.append(pictureModeToggle);

        const picturesCuesRow = new Container({
            class: 'view-panel-row'
        });

        const picturesCuesLabel = new Label({
            text: 'Picture Cues',
            class: 'view-panel-row-label'
        });

        const picturesCuesToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        picturesCuesRow.append(picturesCuesLabel);
        picturesCuesRow.append(picturesCuesToggle);

        const picturesOverlayRow = new Container({
            class: 'view-panel-row'
        });

        const picturesOverlayLabel = new Label({
            text: 'Picture Overlay',
            class: 'view-panel-row-label'
        });

        const picturesOverlayToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        picturesOverlayRow.append(picturesOverlayLabel);
        picturesOverlayRow.append(picturesOverlayToggle);

        const picturesActionsRow = new Container({
            class: 'view-panel-row'
        });

        const picturesActionsLabel = new Label({
            text: 'Pictures',
            class: 'view-panel-row-label'
        });

        const picturesActions = new Container({
            class: 'view-panel-row-pickers'
        });

        const picturesNearestButton = new Button({
            text: 'Nearest',
            class: 'view-panel-row-picker'
        });

        const picturesFocusButton = new Button({
            text: 'Focus',
            class: 'view-panel-row-picker'
        });

        picturesActions.append(picturesNearestButton);
        picturesActions.append(picturesFocusButton);

        picturesActionsRow.append(picturesActionsLabel);
        picturesActionsRow.append(picturesActions);

        const picturesStatusRow = new Container({
            class: 'view-panel-row'
        });

        const picturesStatusLabel = new Label({
            text: 'Pictures: 0 (Selected: —)',
            class: 'view-panel-row-label'
        });

        picturesStatusRow.append(picturesStatusLabel);

        // tonemapping

        const tonemappingRow = new Container({
            class: 'view-panel-row'
        });

        const tonemappingLabel = new Label({
            text: localize('panel.view-options.tonemapping'),
            class: 'view-panel-row-label'
        });

        const tonemappingSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'linear',
            options: [
                { v: 'linear', t: localize('panel.view-options.tonemapping.linear') },
                { v: 'neutral', t: localize('panel.view-options.tonemapping.neutral') },
                { v: 'aces', t: localize('panel.view-options.tonemapping.aces') },
                { v: 'aces2', t: localize('panel.view-options.tonemapping.aces2') },
                { v: 'filmic', t: localize('panel.view-options.tonemapping.filmic') },
                { v: 'hejl', t: localize('panel.view-options.tonemapping.hejl') }
            ]
        });

        tonemappingRow.append(tonemappingLabel);
        tonemappingRow.append(tonemappingSelection);

        // camera fov

        const fovRow = new Container({
            class: 'view-panel-row'
        });

        const fovLabel = new Label({
            text: localize('panel.view-options.fov'),
            class: 'view-panel-row-label'
        });

        const fovSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 10,
            max: 120,
            precision: 1,
            value: 60
        });

        fovRow.append(fovLabel);
        fovRow.append(fovSlider);

        // sh bands
        const shBandsRow = new Container({
            class: 'view-panel-row'
        });

        const shBandsLabel = new Label({
            text: localize('panel.view-options.sh-bands'),
            class: 'view-panel-row-label'
        });

        const shBandsSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0,
            max: 3,
            precision: 0,
            value: 3
        });

        shBandsRow.append(shBandsLabel);
        shBandsRow.append(shBandsSlider);

        // camera fly speed

        const cameraFlySpeedRow = new Container({
            class: 'view-panel-row'
        });

        const cameraFlySpeedLabel = new Label({
            text: localize('panel.view-options.fly-speed'),
            class: 'view-panel-row-label'
        });

        const cameraFlySpeedSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0.1,
            max: 30,
            precision: 1,
            value: 1
        });

        cameraFlySpeedRow.append(cameraFlySpeedLabel);
        cameraFlySpeedRow.append(cameraFlySpeedSlider);

        // splat mode

        const splatModeRow = new Container({
            class: 'view-panel-row'
        });

        const splatModeLabel = new Label({
            text: 'Splat Mode',
            class: 'view-panel-row-label'
        });

        const splatModeSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'centers',
            options: [
                { v: 'centers', t: 'Centers Mode' },
                { v: 'rings', t: 'Rings Mode' }
            ]
        });

        splatModeRow.append(splatModeLabel);
        splatModeRow.append(splatModeSelection);

        // walk speed

        const cameraWalkSpeedRow = new Container({
            class: 'view-panel-row'
        });

        const cameraWalkSpeedLabel = new Label({
            text: 'Walk Speed',
            class: 'view-panel-row-label'
        });

        const cameraWalkSpeedSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0.1,
            max: 15,
            precision: 1,
            value: 10
        });

        cameraWalkSpeedRow.append(cameraWalkSpeedLabel);
        cameraWalkSpeedRow.append(cameraWalkSpeedSlider);

        // proximity guard / collision

        const collisionModeRow = new Container({
            class: 'view-panel-row'
        });

        const collisionModeLabel = new Label({
            text: 'Proximity Guard',
            class: 'view-panel-row-label'
        });

        const collisionModeSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'none',
            options: [
                { v: 'none', t: 'None' },
                { v: 'mesh', t: 'Mesh' },
                { v: 'splats', t: 'Splats' },
                { v: 'all', t: 'All' }
            ]
        });

        collisionModeRow.append(collisionModeLabel);
        collisionModeRow.append(collisionModeSelection);

        const collisionDistanceRow = new Container({
            class: 'view-panel-row'
        });

        const collisionDistanceLabel = new Label({
            text: 'Pushback Distance',
            class: 'view-panel-row-label'
        });

        const collisionDistanceSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0,
            max: 5,
            precision: 2,
            value: 0.6
        });

        collisionDistanceRow.append(collisionDistanceLabel);
        collisionDistanceRow.append(collisionDistanceSlider);

        const collisionSplatOpacityRow = new Container({
            class: 'view-panel-row'
        });

        const collisionSplatOpacityLabel = new Label({
            text: 'Splat Opacity Threshold',
            class: 'view-panel-row-label'
        });

        const collisionSplatOpacitySlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0,
            max: 1,
            precision: 2,
            value: 0.05
        });

        collisionSplatOpacityRow.append(collisionSplatOpacityLabel);
        collisionSplatOpacityRow.append(collisionSplatOpacitySlider);

        // walk controls

        // walk height presets (C.9)

        const walkPresetRow = new Container({
            class: 'view-panel-row'
        });

        const walkPresetLabel = new Label({
            text: 'Walk Preset',
            class: 'view-panel-row-label'
        });

        const walkPresetSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'standing',
            options: []
        });

        walkPresetRow.append(walkPresetLabel);
        walkPresetRow.append(walkPresetSelection);

        const walkPresetButtonsRow = new Container({
            class: 'view-panel-row'
        });

        const walkPresetButtonsLabel = new Label({
            text: '',
            class: 'view-panel-row-label'
        });

        const walkPresetButtons = new Container({
            class: 'view-panel-row-pickers'
        });

        const saveWalkPresetButton = new Button({
            text: 'Save as Preset'
        });

        const deleteWalkPresetButton = new Button({
            text: 'Delete Preset'
        });

        walkPresetButtons.append(saveWalkPresetButton);
        walkPresetButtons.append(deleteWalkPresetButton);

        walkPresetButtonsRow.append(walkPresetButtonsLabel);
        walkPresetButtonsRow.append(walkPresetButtons);

        const walkHeightRow = new Container({
            class: 'view-panel-row'
        });

        const walkHeightLabel = new Label({
            text: 'Walk Height',
            class: 'view-panel-row-label'
        });

        const walkHeightSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0.1,
            max: 10,
            precision: 1,
            value: 1.7
        });

        walkHeightRow.append(walkHeightLabel);
        walkHeightRow.append(walkHeightSlider);

        const walkSnapRow = new Container({
            class: 'view-panel-row'
        });

        const walkSnapLabel = new Label({
            text: 'Snap to Mesh',
            class: 'view-panel-row-label'
        });

        const walkSnapToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: true
        });

        walkSnapRow.append(walkSnapLabel);
        walkSnapRow.append(walkSnapToggle);

        // walk preset state + sync

        type WalkPresetLike = WalkPreset;

        let walkPresets: WalkPresetLike[] = [];
        let selectedWalkPresetId: string = 'standing';

        const updateWalkPresetOptions = (presets: WalkPresetLike[]) => {
            walkPresets = presets;
            walkPresetSelection.options = presets.map(p => ({ v: p.id, t: p.name }));

            // keep selection if possible, otherwise fall back to first option
            const hasSelected = presets.some(p => p.id === selectedWalkPresetId);
            selectedWalkPresetId = hasSelected ? selectedWalkPresetId : (presets[0]?.id ?? 'standing');
            if (walkPresetSelection.value !== selectedWalkPresetId) {
                walkPresetSelection.value = selectedWalkPresetId;
            }

            const selected = presets.find(p => p.id === selectedWalkPresetId);
            deleteWalkPresetButton.disabled = !!selected?.builtin;
        };

        // Initialize presets with built-ins. The editor will re-emit the full list (built-ins +
        // any custom presets) once its event registration runs.
        updateWalkPresetOptions(DEFAULT_PRESETS);

        // control mode

        const controlModeRow = new Container({
            class: 'view-panel-row'
        });

        const controlModeLabel = new Label({
            text: localize('panel.view-options.control-mode'),
            class: 'view-panel-row-label'
        });

        const controlModeSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'orbit',
            options: [
                { v: 'orbit', t: localize('panel.view-options.control-mode.orbit') },
                { v: 'fly', t: localize('panel.view-options.control-mode.fly') },
                { v: 'walk', t: 'Walk' }
            ]
        });

        controlModeRow.append(controlModeLabel);
        controlModeRow.append(controlModeSelection);

        // camera bounds

        const cameraBoundsRow = new Container({
            class: 'view-panel-row'
        });

        const cameraBoundsLabel = new Label({
            text: 'Camera Bounds',
            class: 'view-panel-row-label'
        });

        const cameraBoundsToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        cameraBoundsRow.append(cameraBoundsLabel);
        cameraBoundsRow.append(cameraBoundsToggle);

        const cameraBoundsPaddingRow = new Container({
            class: 'view-panel-row'
        });

        const cameraBoundsPaddingLabel = new Label({
            text: 'Bounds Padding',
            class: 'view-panel-row-label'
        });

        const cameraBoundsPaddingSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 1,
            max: 5,
            precision: 1,
            value: 1.5
        });

        cameraBoundsPaddingRow.append(cameraBoundsPaddingLabel);
        cameraBoundsPaddingRow.append(cameraBoundsPaddingSlider);

        // centers size

        const centersSizeRow = new Container({
            class: 'view-panel-row'
        });

        const centersSizeLabel = new Label({
            text: localize('panel.view-options.centers-size'),
            class: 'view-panel-row-label'
        });

        const centersSizeSlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0,
            max: 10,
            precision: 1,
            value: 2
        });

        centersSizeRow.append(centersSizeLabel);
        centersSizeRow.append(centersSizeSlider);

        // centers gaussian color
        const centersColorRow = new Container({
            class: 'view-panel-row'
        });

        const centersColorLabel = new Label({
            text: localize('panel.view-options.centers-gaussian-color'),
            class: 'view-panel-row-label'
        });

        const centersColorToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        centersColorRow.append(centersColorLabel);
        centersColorRow.append(centersColorToggle);

        // outline selection

        const outlineSelectionRow = new Container({
            class: 'view-panel-row'
        });

        const outlineSelectionLabel = new Label({
            text: localize('panel.view-options.outline-selection'),
            class: 'view-panel-row-label'
        });

        const outlineSelectionToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        outlineSelectionRow.append(outlineSelectionLabel);
        outlineSelectionRow.append(outlineSelectionToggle);

        // show grid

        const showGridRow = new Container({
            class: 'view-panel-row'
        });

        const showGridLabel = new Label({
            text: localize('panel.view-options.show-grid'),
            class: 'view-panel-row-label'
        });

        const showGridToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: true
        });

        showGridRow.append(showGridLabel);
        showGridRow.append(showGridToggle);

        // helper gizmos

        const helperGizmosRow = new Container({
            class: 'view-panel-row'
        });

        const helperGizmosLabel = new Label({
            text: 'Helper Gizmos',
            class: 'view-panel-row-label'
        });

        const helperGizmosToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: !!events.invoke('helpers.gizmos')
        });

        helperGizmosRow.append(helperGizmosLabel);
        helperGizmosRow.append(helperGizmosToggle);

        // show bound

        const showBoundRow = new Container({
            class: 'view-panel-row'
        });

        const showBoundLabel = new Label({
            text: localize('panel.view-options.show-bound'),
            class: 'view-panel-row-label'
        });

        const showBoundToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: true
        });

        showBoundRow.append(showBoundLabel);
        showBoundRow.append(showBoundToggle);

        // clip box enabled

        const clipEnabledRow = new Container({
            class: 'view-panel-row'
        });

        const clipEnabledLabel = new Label({
            text: 'Clip Box',
            class: 'view-panel-row-label'
        });

        const clipEnabledToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            // Default OFF; will be synchronized by the initial `clipBox.changed` event.
            value: false
        });

        clipEnabledRow.append(clipEnabledLabel);
        clipEnabledRow.append(clipEnabledToggle);

        // measure picking options

        const measureModeRow = new Container({
            class: 'view-panel-row'
        });

        const measureModeLabel = new Label({
            text: 'Measure Picking',
            class: 'view-panel-row-label'
        });

        const measureModeSelection = new SelectInput({
            class: 'view-panel-row-select',
            defaultValue: 'normal',
            options: [
                { v: 'normal', t: 'Normal (surface)' },
                { v: 'center', t: 'Splat center' },
                { v: 'none', t: 'Mesh only' }
            ]
        });

        measureModeRow.append(measureModeLabel);
        measureModeRow.append(measureModeSelection);

        const measureOpacityRow = new Container({
            class: 'view-panel-row'
        });

        const measureOpacityLabel = new Label({
            text: 'Measure Min Opacity',
            class: 'view-panel-row-label'
        });

        const measureOpacitySlider = new SliderInput({
            class: 'view-panel-row-slider',
            min: 0,
            max: 1,
            precision: 2,
            value: 0.5
        });

        measureOpacityRow.append(measureOpacityLabel);
        measureOpacityRow.append(measureOpacitySlider);

        const measureHistoryRow = new Container({
            class: 'view-panel-row'
        });

        const measureHistoryLabel = new Label({
            text: 'Measure History',
            class: 'view-panel-row-label'
        });

        const measureHistoryValue = new Label({
            text: '',
            class: 'view-panel-row-label'
        });

        measureHistoryRow.append(measureHistoryLabel);
        measureHistoryRow.append(measureHistoryValue);


        // show camera poses

        const showCameraPosesRow = new Container({
            class: 'view-panel-row'
        });

        const showCameraPosesLabel = new Label({
            text: localize('panel.view-options.show-camera-poses'),
            class: 'view-panel-row-label'
        });

        const showCameraPosesToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: false
        });

        showCameraPosesRow.append(showCameraPosesLabel);
        showCameraPosesRow.append(showCameraPosesToggle);

        // show bookmark markers

        const showBookmarkMarkersRow = new Container({
            class: 'view-panel-row'
        });

        const showBookmarkMarkersLabel = new Label({
            text: 'Bookmark Cameras',
            class: 'view-panel-row-label'
        });

        const showBookmarkMarkersToggle = new BooleanInput({
            type: 'toggle',
            class: 'view-panel-row-toggle',
            value: true
        });

        showBookmarkMarkersRow.append(showBookmarkMarkersLabel);
        showBookmarkMarkersRow.append(showBookmarkMarkersToggle);

        this.append(header);
        this.append(clrRow);
        this.append(pictureModeRow);
        this.append(picturesCuesRow);
        this.append(picturesOverlayRow);
        this.append(picturesActionsRow);
        this.append(picturesStatusRow);
        this.append(tonemappingRow);
        this.append(fovRow);
        this.append(shBandsRow);
        this.append(cameraFlySpeedRow);
        this.append(splatModeRow);
        this.append(cameraWalkSpeedRow);
        this.append(walkPresetRow);
        this.append(walkPresetButtonsRow);
        this.append(collisionModeRow);
        this.append(collisionDistanceRow);
        this.append(collisionSplatOpacityRow);
        this.append(walkHeightRow);
        this.append(walkSnapRow);
        this.append(controlModeRow);
        this.append(cameraBoundsRow);
        this.append(cameraBoundsPaddingRow);
        this.append(centersSizeRow);
        this.append(centersColorRow);
        this.append(outlineSelectionRow);
        this.append(showGridRow);
        this.append(helperGizmosRow);
        this.append(showBoundRow);
        this.append(clipEnabledRow);
        this.append(measureModeRow);
        this.append(measureOpacityRow);
        this.append(measureHistoryRow);
        this.append(showCameraPosesRow);
        this.append(showBookmarkMarkersRow);

        // handle panel visibility

        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                this.hidden = !visible;
                events.fire('viewPanel.visible', visible);
            }
        };

        events.function('viewPanel.visible', () => {
            return !this.hidden;
        });

        events.on('viewPanel.setVisible', (visible: boolean) => {
            setVisible(visible);
        });

        events.on('viewPanel.toggleVisible', () => {
            setVisible(this.hidden);
        });

        events.on('colorPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        // picture mode gating and controls

        const updatePictureAvailability = () => {
            const needsValidation = !!events.invoke('camera.posesNeedsValidation');
            pictureModeToggle.disabled = needsValidation;

            const usable = !needsValidation && pictureModeToggle.value;
            picturesCuesToggle.disabled = !usable;
            picturesOverlayToggle.disabled = !usable;
            picturesNearestButton.disabled = !usable;
            picturesFocusButton.disabled = !usable;
        };

        const updatePicturesStatus = () => {
            const count = (events.invoke('pictures.count') as number) ?? 0;
            const selected = events.invoke('pictures.selected') as any;
            const name = selected?.name ? String(selected.name) : '—';
            picturesStatusLabel.text = `Pictures: ${count} (Selected: ${name})`;
        };

        events.on('camera.posesValidationChanged', () => updatePictureAvailability());
        events.on('picture.enabledChanged', (enabled: boolean) => {
            pictureModeToggle.value = !!enabled;
            updatePictureAvailability();
        });
        events.on('pictures.cues.enabledChanged', (enabled: boolean) => {
            picturesCuesToggle.value = !!enabled;
        });
        events.on('pictures.overlay.enabledChanged', (enabled: boolean) => {
            picturesOverlayToggle.value = !!enabled;
        });
        events.on('pictures.selectedChanged', updatePicturesStatus);
        events.on('pictures.listChanged', updatePicturesStatus);

        pictureModeToggle.on('change', () => {
            events.fire('picture.setEnabled', pictureModeToggle.value);
            pictureModeToggle.value = !!events.invoke('picture.enabled');
            updatePictureAvailability();
        });

        picturesCuesToggle.on('change', () => {
            events.fire('pictures.cues.setEnabled', picturesCuesToggle.value);
            picturesCuesToggle.value = !!events.invoke('pictures.cues.enabled');
        });

        picturesOverlayToggle.on('change', () => {
            events.fire('pictures.overlay.setEnabled', picturesOverlayToggle.value);
            picturesOverlayToggle.value = !!events.invoke('pictures.overlay.enabled');
        });

        picturesNearestButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('pictures.selectNearest');
        });

        picturesFocusButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('pictures.focusSelected');
        });

        // Defer initial state reads – the event functions are not registered yet
        // when the UI is constructed.  The "hack: fire events" block at the end of
        // registerEditorEvents / registerPicturesEvents will push the correct
        // initial values through the event listeners above.
        picturesCuesToggle.disabled = true;
        picturesOverlayToggle.disabled = true;
        picturesNearestButton.disabled = true;
        picturesFocusButton.disabled = true;

        // sh bands

        events.on('view.bands', (bands: number) => {
            shBandsSlider.value = bands;
        });

        shBandsSlider.on('change', (value: number) => {
            events.fire('view.setBands', value);
        });

        // splat size

        events.on('camera.splatSize', (value: number) => {
            centersSizeSlider.value = value;
        });

        centersSizeSlider.on('change', (value: number) => {
            events.fire('camera.setSplatSize', value);
            events.fire('camera.setOverlay', true);
            events.fire('camera.setMode', 'centers');
        });

        // centers gaussian color
        events.on('view.centersUseGaussianColor', (value: boolean) => {
            centersColorToggle.value = value;
        });

        centersColorToggle.on('change', (value: boolean) => {
            events.fire('view.setCentersUseGaussianColor', value);
        });

        // camera speed

        events.on('camera.flySpeed', (value: number) => {
            cameraFlySpeedSlider.value = value;
        });

        cameraFlySpeedSlider.on('change', (value: number) => {
            events.fire('camera.setFlySpeed', value);
        });

        events.on('camera.mode', (mode: string) => {
            splatModeSelection.value = mode;
        });

        splatModeSelection.on('change', (value: string) => {
            events.fire('camera.setMode', value);
            events.fire('camera.setOverlay', true);
        });

        events.on('camera.walkSpeed', (value: number) => {
            cameraWalkSpeedSlider.value = value;
        });

        cameraWalkSpeedSlider.on('change', (value: number) => {
            events.fire('camera.setWalkSpeed', value);
        });

        events.on('camera.collisionSources', (value: string) => {
            collisionModeSelection.value = value;
        });
        events.on('camera.collisionDistance', (value: number) => {
            collisionDistanceSlider.value = value;
        });
        events.on('camera.collisionMinSplatOpacity', (value: number) => {
            collisionSplatOpacitySlider.value = value;
        });

        // walk presets

        events.on('walk.presets', (presets: WalkPresetLike[]) => {
            updateWalkPresetOptions(presets ?? []);
        });

        walkPresetSelection.on('change', (presetId: string) => {
            selectedWalkPresetId = presetId;
            const preset = walkPresets.find(p => p.id === presetId);
            deleteWalkPresetButton.disabled = !!preset?.builtin;
            if (preset) {
                walkHeightSlider.value = preset.heightMeters;
                events.fire('camera.setWalkHeight', preset.heightMeters);
            }
        });

        saveWalkPresetButton.on('click', async () => {
            const heightMeters = walkHeightSlider.value as number;
            const result = await events.invoke('showPopup', {
                type: 'okcancel',
                header: 'Save Walk Preset',
                message: 'Preset name:',
                input: {
                    placeholder: 'e.g. Investigator (Seated)',
                    value: ''
                }
            });

            if (result?.action !== 'ok') return;
            const name = (result?.value ?? '').toString();
            const preset = await events.invoke('walk.addPreset', name, heightMeters);
            if (preset?.id) {
                selectedWalkPresetId = preset.id;
                walkPresetSelection.value = preset.id;
            }
        });

        deleteWalkPresetButton.on('click', async () => {
            const preset = walkPresets.find(p => p.id === selectedWalkPresetId);
            if (!preset || preset.builtin) {
                await events.invoke('showPopup', {
                    type: 'info',
                    header: 'Cannot delete preset',
                    message: 'Built-in presets cannot be deleted.'
                });
                return;
            }

            const confirm = await events.invoke('showPopup', {
                type: 'yesno',
                header: 'Delete preset?',
                message: `Delete '${preset.name}'?`
            });

            if (confirm?.action !== 'yes') return;
            selectedWalkPresetId = 'standing';
            await events.invoke('walk.removePreset', preset.id);
        });

        collisionModeSelection.on('change', (value: string) => {
            events.fire('camera.setCollisionSources', value);
        });

        collisionDistanceSlider.on('change', (value: number) => {
            events.fire('camera.setCollisionDistance', value);
        });

        collisionSplatOpacitySlider.on('change', (value: number) => {
            events.fire('camera.setCollisionMinSplatOpacity', value);
        });

        events.on('camera.walkHeight', (value: number) => {
            walkHeightSlider.value = value;
        });

        walkHeightSlider.on('change', (value: number) => {
            events.fire('camera.setWalkHeight', value);
        });

        events.on('camera.walkSnapToMesh', (enabled: boolean) => {
            walkSnapToggle.value = !!enabled;
        });

        walkSnapToggle.on('change', (value: boolean) => {
            events.fire('camera.setWalkSnapToMesh', value);
        });

        // control mode

        events.on('camera.controlMode', (mode: string) => {
            controlModeSelection.value = mode;
        });

        controlModeSelection.on('change', (value: 'orbit' | 'fly' | 'walk') => {
            events.fire('camera.setControlMode', value);
        });

        // camera bounds

        events.on('camera.boundsEnabled', (value: boolean) => {
            cameraBoundsToggle.value = !!value;
            cameraBoundsPaddingRow.hidden = !value;
        });

        cameraBoundsToggle.on('change', (value: boolean) => {
            events.fire('camera.setBoundsEnabled', value);
        });

        events.on('camera.boundsPadding', (value: number) => {
            cameraBoundsPaddingSlider.value = value;
        });

        cameraBoundsPaddingSlider.on('change', (value: number) => {
            events.fire('camera.setBoundsPadding', value);
        });

        // outline selection

        events.on('view.outlineSelection', (value: boolean) => {
            outlineSelectionToggle.value = value;
        });

        outlineSelectionToggle.on('change', (value: boolean) => {
            events.fire('view.setOutlineSelection', value);
        });

        // show grid

        events.on('grid.visible', (visible: boolean) => {
            showGridToggle.value = visible;
        });

        showGridToggle.on('change', () => {
            events.fire('grid.setVisible', showGridToggle.value);
        });

        // helper gizmos

        let suppressHelperGizmosChange = false;
        events.on('helpers.gizmos', (show: boolean) => {
            if (helperGizmosToggle.value === !!show) return;
            suppressHelperGizmosChange = true;
            helperGizmosToggle.value = !!show;
            suppressHelperGizmosChange = false;
        });

        helperGizmosToggle.on('change', () => {
            if (suppressHelperGizmosChange) return;
            events.fire('helpers.gizmos', helperGizmosToggle.value);
        });

        // show bound

        events.on('camera.bound', (visible: boolean) => {
            showBoundToggle.value = visible;
        });

        showBoundToggle.on('change', () => {
            events.fire('camera.setBound', showBoundToggle.value);
        });

        // clip box

        events.on('clipBox.changed', (clipBox: any) => {
            clipEnabledToggle.value = !!clipBox?.enabled;
        });

        clipEnabledToggle.on('change', (value: boolean) => {
            events.fire('clipBox.set', { enabled: !!value });
        });

        // measure controls

        events.on('measure.splatPickMode', (mode: string) => {
            measureModeSelection.value = mode;
        });

        measureModeSelection.on('change', (value: string) => {
            events.fire('measure.setSplatPickMode', value);
        });

        events.on('measure.minSplatOpacity', (value: number) => {
            measureOpacitySlider.value = value;
        });

        measureOpacitySlider.on('change', (value: number) => {
            events.fire('measure.setMinSplatOpacity', value);
        });

        const formatHistory = (arr: number[]) => {
            if (!arr || arr.length === 0) return '';
            return arr.slice(0, 5).map(v => Number(v).toFixed(2)).join(', ');
        };

        events.on('measure.historyChanged', (arr: number[]) => {
            measureHistoryValue.text = formatHistory(arr);
        });

        measureHistoryValue.text = '';

        // show camera poses

        events.on('camera.showPoses', (visible: boolean) => {
            showCameraPosesToggle.value = visible;
        });

        showCameraPosesToggle.on('change', () => {
            events.fire('camera.setShowPoses', showCameraPosesToggle.value);
        });

        // show bookmark markers

        events.on('bookmarks.showMarkers', (visible: boolean) => {
            showBookmarkMarkersToggle.value = visible;
        });

        showBookmarkMarkersToggle.on('change', () => {
            events.fire('bookmarks.setShowMarkers', showBookmarkMarkersToggle.value);
        });

        // background color

        bgClrPicker.on('change', (value: number[]) => {
            events.fire('setBgClr', new Color(value[0], value[1], value[2]));
        });

        selectedClrPicker.on('change', (value: number[]) => {
            events.fire('setSelectedClr', new Color(value[0], value[1], value[2], value[3]));
        });

        unselectedClrPicker.on('change', (value: number[]) => {
            events.fire('setUnselectedClr', new Color(value[0], value[1], value[2], value[3]));
        });

        lockedClrPicker.on('change', (value: number[]) => {
            events.fire('setLockedClr', new Color(value[0], value[1], value[2], value[3]));
        });

        // camera fov

        events.on('camera.fov', (fov: number) => {
            fovSlider.value = fov;
        });

        fovSlider.on('change', (value: number) => {
            events.fire('camera.setFov', value);
        });

        // tonemapping

        events.on('camera.tonemapping', (tonemapping: string) => {
            tonemappingSelection.value = tonemapping;
        });

        tonemappingSelection.on('change', (value: string) => {
            events.fire('camera.setTonemapping', value);
        });

        // tooltips
        const shortcutManager: ShortcutManager = events.invoke('shortcutManager');
        const shortcut = shortcutManager.formatShortcut('grid.toggleVisible');
        tooltips.register(showGridLabel, `${localize('panel.view-options.show-grid')} ( ${shortcut} )`, 'left');
        tooltips.register(bgClrPicker, localize('panel.view-options.background-color'), 'left');
        tooltips.register(selectedClrPicker, localize('panel.view-options.selected-color'), 'top');
        tooltips.register(unselectedClrPicker, localize('panel.view-options.unselected-color'), 'top');
        tooltips.register(lockedClrPicker, localize('panel.view-options.locked-color'), 'top');
    }
}

export { ViewPanel };
